import { Box, Link, Table, TableBody, TableCell, TableHead, TableRow } from '@mui/material';
import { ReactElement, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { Link as RouterLink } from 'react-router-dom';
import { imageBase } from '../../api/client';
import { useMarketTerm } from '../../hooks/useMarketTerm';
import { TOTAL_SERIES, getProductSummary, getProductUtilizationAll, utilizationStats } from '../../redux/slices/tools/toolsSlice';
import { cellSx, formatPct, headSx } from './ManufacturerSummary';
import { UtilizationSparkline } from './UtilizationSparkline';
import { NOT_TRACKED_STATUSES, TRACKED_STATUSES, itemsLink } from './itemsLink';

type ProductSummaryProps = { fetching: boolean; category: string };

// One row per product, narrowed to the product category selected in the
// equipment table above (or every category when that selection is Total). The
// manufacturer logo and name sit between the category and the product so a
// long list still reads left to right: what kind, whose, which. Counts link
// into the item list the same way the manufacturer view does.
export const ProductSummary = ({ fetching, category }: ProductSummaryProps): ReactElement => {
  const term = useMarketTerm();
  const summary = useSelector(getProductSummary);
  const series = useSelector(getProductUtilizationAll);

  const rows = useMemo(() => summary(category), [summary, category]);
  const scope = category === TOTAL_SERIES ? undefined : category;

  return (
    <Table size="small">
      <TableHead>
        <TableRow>
          <TableCell sx={headSx}>Category</TableCell>
          <TableCell sx={{ ...headSx, width: 64 }} />
          <TableCell sx={headSx}>Manufacturer</TableCell>
          <TableCell sx={headSx}>Product</TableCell>
          <TableCell sx={headSx} align="right">
            Items
          </TableCell>
          <TableCell sx={headSx} align="right">
            {term('Tracked')}
          </TableCell>
          <TableCell sx={headSx} align="right">
            {term('Not Tracked')}
          </TableCell>
          <TableCell sx={headSx} align="right">
            {term('Tracked')} %
          </TableCell>
          <TableCell sx={headSx} align="right">
            Max %
          </TableCell>
          <TableCell sx={headSx} align="right">
            Avg %
          </TableCell>
          <TableCell sx={{ ...headSx, width: '26%' }}>Utilization, past 12 months</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {!fetching && !rows.length && (
          <TableRow>
            <TableCell sx={cellSx} colSpan={11}>
              No products in this category.
            </TableCell>
          </TableRow>
        )}
        {rows.map((row) => {
          const points = series[row.productId] || [];
          const stats = utilizationStats(points);
          return (
            <TableRow key={row.productId} hover>
              <TableCell sx={cellSx}>{row.category}</TableCell>
              <TableCell sx={{ ...cellSx, width: 64 }}>
                {!!row.imageKey && (
                  <Box
                    component="img"
                    src={`${imageBase(row.imageKey)}?height=48`}
                    alt=""
                    sx={{ height: 24, maxWidth: 56, objectFit: 'contain', display: 'block' }}
                  />
                )}
              </TableCell>
              <TableCell sx={cellSx}>
                <Link component={RouterLink} to={`/tools/manufacturers/${row.manufacturerId}/details`} underline="hover" sx={{ fontSize: 13 }}>
                  {row.manufacturerName}
                </Link>
              </TableCell>
              <TableCell sx={{ ...cellSx, whiteSpace: 'normal' }}>
                <Link component={RouterLink} to={`/tools/products/${row.productId}/details`} underline="hover" sx={{ fontSize: 13 }}>
                  {row.productName}
                </Link>
              </TableCell>
              <TableCell sx={cellSx} align="right">
                <Link component={RouterLink} to={itemsLink({ category: scope, productId: row.productId })} underline="hover" color="inherit">
                  {row.items.toLocaleString()}
                </Link>
              </TableCell>
              <TableCell sx={cellSx} align="right">
                <Link
                  component={RouterLink}
                  to={itemsLink({ category: scope, productId: row.productId, scanStatus: TRACKED_STATUSES })}
                  underline="hover"
                  color="inherit"
                >
                  {row.tracked.toLocaleString()}
                </Link>
              </TableCell>
              <TableCell sx={cellSx} align="right">
                <Link
                  component={RouterLink}
                  to={itemsLink({ category: scope, productId: row.productId, scanStatus: NOT_TRACKED_STATUSES })}
                  underline="hover"
                  color="inherit"
                >
                  {row.notTracked.toLocaleString()}
                </Link>
              </TableCell>
              <TableCell sx={cellSx} align="right">
                {formatPct(row.trackedPct)}
              </TableCell>
              <TableCell sx={cellSx} align="right">
                {stats ? formatPct(stats.max) : '—'}
              </TableCell>
              <TableCell sx={cellSx} align="right">
                {stats ? formatPct(stats.avg) : '—'}
              </TableCell>
              <TableCell sx={{ ...cellSx, px: 2, width: '26%' }}>
                <UtilizationSparkline points={points} />
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
};
